import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ArrowRight,
  BarChart3,
  Sparkles,
  Star,
  Target,
  TrendingUp,
  Zap,
} from 'lucide-react';
import * as THREE from 'three';
import NET from 'vanta/dist/vanta.net.min';
import { Button } from '../../../components/ui/button';
import useAuth from '../../../hooks/useAuth';
import { cn } from '../../../lib/utils';
import growLogo from '../../../assets/images/logo-purple.svg';

const FEATURES = [
  {
    icon: Target,
    title: 'Skill Tracking',
    description: 'Monitor and develop employee competencies with structured skill trees',
  },
  {
    icon: TrendingUp,
    title: 'Career Growth',
    description: 'Define clear career paths and level progression for your team',
  },
  {
    icon: Star,
    title: 'Performance Reviews',
    description: 'Streamlined review process with comprehensive feedback tools',
  },
  {
    icon: BarChart3,
    title: 'Analytics Dashboard',
    description: 'Data-driven insights into team development and progress',
  },
];


function FeatureCard({ icon: Icon, title, description, index }) {
  return (
    <div
      className={cn(
        'group relative p-6 rounded-2xl transition-all duration-500',
        'bg-white/60 backdrop-blur-sm border border-white/20',
        'hover:bg-white/80 hover:shadow-xl hover:shadow-primary/5',
        'hover:-translate-y-1',
      )}
      style={{
        animationDelay: `${index * 100}ms`,
      }}
    >
      <div className="flex items-start gap-4">
        <div
          className={cn(
            'p-3 rounded-xl transition-all duration-300',
            'bg-primary/10 group-hover:bg-primary group-hover:scale-110',
          )}
        >
          <Icon
            className={cn(
              'h-6 w-6 transition-colors duration-300',
              'text-primary group-hover:text-white',
            )}
          />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-foreground mb-1">{title}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
        </div>
      </div>
    </div>
  );
}

FeatureCard.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
};

function FloatingShape({ className, delay }) {
  return (
    <div
      className={cn(
        'absolute rounded-full opacity-20 blur-3xl animate-pulse',
        className,
      )}
      style={{
        animationDelay: `${delay}ms`,
        animationDuration: '4s',
      }}
    />
  );
}

FloatingShape.propTypes = {
  className: PropTypes.string.isRequired,
  delay: PropTypes.number,
};

FloatingShape.defaultProps = {
  delay: 0,
};

function LoginPageV3() {
  const { loginUser } = useAuth();
  const vantaRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let vantaEffect;

    const initializeVanta = () => {
      if (!vantaRef.current) return;

      vantaEffect = NET({
        el: vantaRef.current,
        THREE,
        mouseControls: true,
        touchControls: true,
        gyroControls: false,
        minHeight: 500.0,
        minWidth: 300.0,
        scale: 1.0,
        scaleMobile: 1.0,
        color: 0x7553ea,
        backgroundColor: 0xfafafa,
        points: 8.0,
        maxDistance: 18.0,
        spacing: 18.0,
        showDots: true,
      });
    };

    initializeVanta();

    return () => {
      if (vantaEffect) vantaEffect.destroy();
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await loginUser();
    } catch (error) {
      console.error('Login failed:', error);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Vanta Background - Only on left side */}
      <div
        ref={vantaRef}
        className="absolute inset-0 z-0"
        style={{
          background: 'linear-gradient(135deg, #fafafa 0%, #f0f0f0 100%)',
        }}
      />

      {/* Floating decorative shapes */}
      <FloatingShape className="w-96 h-96 bg-primary -top-20 -left-20" delay={0} />
      <FloatingShape className="w-64 h-64 bg-purple-400 top-1/3 right-1/4" delay={1000} />
      <FloatingShape className="w-80 h-80 bg-indigo-400 -bottom-20 left-1/3" delay={2000} />

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex">
        {/* Left Side - Hero Section */}
        <div className="flex-1 flex flex-col justify-center items-center lg:items-start px-8 lg:pl-48 lg:pr-0 py-12">
          <div className="max-w-xl w-full">
            {/* Logo */}
            <div className="flex items-center gap-3 mb-8">
              <img src={growLogo} alt="Grow" className="h-12 w-auto" />
            </div>

            {/* Badge */}
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
              <Sparkles className="h-4 w-4" />
              <span>Employee Growth Platform</span>
            </div>

            {/* Main Heading */}
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-foreground leading-tight mb-6">
              Empower Your Team&apos;s{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-purple-600">
                Professional Growth
              </span>
            </h1>

            {/* Subheading */}
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Track skills, manage career progression, and conduct meaningful performance reviews.
              Everything you need to nurture talent in one unified platform.
            </p>

            {/* CTA Button */}
            <form onSubmit={handleLogin} className="flex flex-col sm:flex-row gap-4 mb-12">
              <Button
                type="submit"
                size="lg"
                className={cn(
                  'h-14 px-8 text-base font-semibold rounded-xl',
                  'bg-gradient-to-r from-primary to-purple-600',
                  'hover:opacity-90 transition-all duration-300',
                  'shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30',
                  'group',
                )}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Connecting...
                  </>
                ) : (
                  <>
                    Sign in with SSO
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </Button>
            </form>

            {/* Trust Indicators */}
            <div className="flex items-center gap-6 text-muted-foreground">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span className="text-sm">Secure SSO Authentication</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-green-500" />
                <span className="text-sm">Enterprise Ready</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Features */}
        <div className="hidden lg:flex flex-1 items-center justify-center px-8 lg:px-16 py-12">
          <div className="w-full max-w-lg space-y-4">
            {/* Features Header */}
            <div className="mb-8">
              <div className="flex items-center gap-2 text-primary font-medium mb-2">
                <Zap className="h-4 w-4" />
                <span>Platform Features</span>
              </div>
              <h2 className="text-2xl font-bold text-foreground">
                Everything you need to grow your team
              </h2>
            </div>

            {/* Feature Cards */}
            {FEATURES.map((feature, index) => (
              <FeatureCard
                key={feature.title}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
                index={index}
              />
            ))}

          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0 z-10 px-8 py-4">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>&copy; {new Date().getFullYear()} Grow. All rights reserved.</span>
          <div className="flex items-center gap-6">
            <button type="button" className="hover:text-foreground transition-colors">
              Onix-Systems
            </button>
            <button type="button" className="hover:text-foreground transition-colors">
              Contact Support
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPageV3;
